import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import { Switch } from "@/components/ui/switch";
import { ArticlesQuickCard } from "@/components/admin/ArticlesQuickCard";
import { TestimonialsAdmin } from "@/components/admin/TestimonialsAdmin";
import { ContentAdmin } from "@/components/admin/ContentAdmin";
import { HealthPanel } from "@/components/admin/HealthPanel";
import { BrewekQuickCard } from "@/components/admin/BrewekQuickCard";
import { ChangePassword } from "@/components/admin/ChangePassword";

const field = "w-full rounded-xl border border-white/10 bg-ink px-4 py-3 text-paper outline-none transition-colors focus:border-volt";

const RateRow = ({ rate, onSaved }) => {
  const [value, setValue] = useState(rate.rate);
  const [active, setActive] = useState(rate.active);
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/rates/${rate.key}`, { rate: Number(value), active });
      onSaved(data);
      toast.success(`Rate ${rate.name} disimpan`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <div data-testid={`admin-rate-row-${rate.key}`} className="grid grid-cols-2 items-center gap-4 border-b border-white/[0.07] py-5 md:grid-cols-12">
      <span className="col-span-2 font-display text-lg text-paper md:col-span-4">{rate.name}</span>
      <div className="flex items-center gap-2 md:col-span-3">
        <input data-testid={`admin-rate-input-${rate.key}`} type="number" min="0" max="100" step="0.5" value={value} onChange={(e) => setValue(e.target.value)} className={field} />
        <span className="text-zinc-400">%</span>
      </div>
      <label className="flex items-center gap-2 text-sm text-zinc-300 md:col-span-3">
        <Switch data-testid={`admin-rate-active-${rate.key}`} checked={active} onCheckedChange={setActive} className="data-[state=checked]:bg-volt data-[state=unchecked]:!bg-zinc-700" /> {active ? "Tampil" : "Disembunyikan"}
      </label>
      <button data-testid={`admin-rate-save-${rate.key}`} onClick={save} className="col-span-2 rounded-full bg-volt px-5 py-2.5 text-sm font-semibold text-ink transition-transform hover:scale-[1.03] md:col-span-2">Simpan</button>
    </div>
  );
};

const SETTING_FIELDS = [
  ["whatsapp", "Nomor WhatsApp CS (format 628xxx)"],
  ["email", "Email CS"],
  ["hours", "Jam layanan (contoh: Setiap hari 08.00–22.00 WIB)"],
  ["announcement", "Pengumuman di atas website (kosongkan jika tidak ada)"],
  ["ga_measurement_id", "Google Analytics — Measurement ID (format G-XXXXXXX, kosongkan jika belum ada)"],
];

const STORE_FIELDS = [
  ["playstore", "Google Play", "https://play.google.com/store/apps/details?id=..."],
  ["appstore", "App Store", "https://apps.apple.com/id/app/..."],
];

const StoreField = ({ k, label, placeholder, form, setForm }) => (
  <div className="rounded-2xl border border-white/10 p-4" data-testid={`admin-store-${k}`}>
    <div className="flex items-center justify-between gap-3">
      <span className="font-display text-paper">Tombol {label}</span>
      <label className="flex items-center gap-2 text-sm text-zinc-300">
        <Switch data-testid={`admin-setting-${k}_enabled`} checked={!!form[`${k}_enabled`]} onCheckedChange={(v) => setForm({ ...form, [`${k}_enabled`]: v })} className="data-[state=checked]:bg-volt data-[state=unchecked]:!bg-zinc-700" />
        {form[`${k}_enabled`] ? "Aktif" : "Segera hadir"}
      </label>
    </div>
    <input data-testid={`admin-setting-${k}_url`} placeholder={placeholder} value={form[`${k}_url`] ?? ""} onChange={(e) => setForm({ ...form, [`${k}_url`]: e.target.value })} className={`mt-3 ${field}`} />
  </div>
);

const SettingsForm = () => {
  const [form, setForm] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      toast.success("Pengaturan disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  if (!form) return null;
  return (
    <form onSubmit={save} data-testid="admin-settings-form" className="space-y-5">
      {SETTING_FIELDS.map(([k, label]) => (
        <div key={k}>
          <label className="block text-sm text-zinc-300">{label}</label>
          <input data-testid={`admin-setting-${k}`} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`mt-2 ${field}`} />
        </div>
      ))}
      <p className="pt-2 text-sm text-zinc-400">Tombol download. Saat dimatikan, tombol tampil sebagai "Segera hadir" dan tidak bisa diklik.</p>
      {STORE_FIELDS.map(([k, label, ph]) => (
        <StoreField key={k} k={k} label={label} placeholder={ph} form={form} setForm={setForm} />
      ))}
      <button data-testid="admin-settings-save" className="rounded-full bg-volt px-6 py-3 font-semibold text-ink transition-transform hover:scale-[1.03]">Simpan pengaturan</button>
    </form>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [rates, setRates] = useState([]);
  useEffect(() => {
    api.get("/admin/rates").then((r) => setRates(Array.isArray(r.data) ? r.data : [])).catch(() => setRates([]));
  }, []);
  const onSaved = (d) => setRates((rs) => rs.map((r) => (r.key === d.key ? d : r)));
  return (
    <div className="grain min-h-screen bg-ink text-paper" data-testid="admin-dashboard">
      <header className="border-b border-white/[0.07]">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5">
          <Logo />
          <div className="flex items-center gap-4 text-sm">
            <a href="/tigasatu/artikel" data-testid="admin-articles-link" className="rounded-full border border-white/15 px-4 py-2 text-zinc-200 hover:border-volt">Artikel</a>
            <a href="/tigasatu/brewek" data-testid="admin-gacha-link" className="rounded-full bg-volt px-4 py-2 font-semibold text-ink">Brewek</a>
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="hidden items-center gap-1 text-zinc-300 hover:text-paper sm:flex">Lihat website <ExternalLink className="h-4 w-4" /></a>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-zinc-200 hover:border-volt">
              <LogOut className="h-4 w-4" /> Keluar
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-12">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Halo, {user?.email}</p>
        <BrewekQuickCard />
        <ArticlesQuickCard />
        <h1 className="mt-12 font-display text-4xl font-bold">Kesehatan server</h1>
        <p className="mt-2 text-zinc-400">Info perawatan: Python, MongoDB, SSL, update sistem, dan backup.</p>
        <HealthPanel />
        <h2 className="mt-16 font-display text-3xl font-bold">Kelola rate</h2>
        <p className="mt-2 text-zinc-400">Perubahan langsung tampil di kalkulator dan tabel rate website.</p>
        <section className="mt-8 rounded-3xl bg-surface p-6 ring-1 ring-white/[0.07] md:p-8" data-testid="admin-rates-panel">
          {rates.map((r) => <RateRow key={r.key} rate={r} onSaved={onSaved} />)}
        </section>
        <h2 className="mt-16 font-display text-3xl font-bold">Kontak & pengumuman</h2>
        <section className="mt-6 rounded-3xl bg-surface p-6 ring-1 ring-white/[0.07] md:p-8">
          <SettingsForm />
        </section>
        <h2 className="mt-16 font-display text-3xl font-bold">Teks website</h2>
        <p className="mt-2 text-zinc-400">Running text, FAQ, Privacy Policy, dan Terms and Conditions.</p>
        <div className="mt-6">{rates.length ? <ContentAdmin rates={rates} /> : null}</div>
        <h2 className="mt-16 font-display text-3xl font-bold">Testimoni Play Store</h2>
        <section className="mt-6 rounded-3xl bg-surface p-6 ring-1 ring-white/[0.07] md:p-8">
          <TestimonialsAdmin />
        </section>
        <h2 className="mt-16 font-display text-3xl font-bold">Ganti password</h2>
        <section className="mt-6 max-w-lg rounded-3xl bg-surface p-6 ring-1 ring-white/[0.07] md:p-8">
          <ChangePassword />
        </section>
      </main>
    </div>
  );
}
